// app/service/fractional-indexing.ts

export const ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

// Precomputed character rank lookup map for high-speed O(1) performance inside loops
export const CHAR_INDEX: Record<string, number> = Object.fromEntries(
  ALPHABET.split("").map((char, index) => [char, index]),
);

// URL-safe alphabet for tie-breaker suffixes to keep DB keys, URLs, and logs clean
export const SUFFIX_ALPHABET =
  "-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

// Precomputed character rank lookup map for high-speed O(1) performance on suffixes
export const SUFFIX_CHAR_INDEX: Record<string, number> = Object.fromEntries(
  SUFFIX_ALPHABET.split("").map((char, index) => [char, index]),
);

// Active Epoch prefix for seamless future data migrations and schema isolation
export const EPOCH = "v1:";

export function stripEpoch(pos: string | null | undefined): string | null {
  if (!pos) return null;
  if (pos.startsWith(EPOCH)) {
    return pos.substring(EPOCH.length);
  }
  return pos;
}

export function addEpoch(pos: string): string {
  return `${EPOCH}${pos}`;
}

/**
 * Safely compares two Base62 bases character-by-character using our custom alphabet ranks.
 * This ensures that ordering is mathematically correct under our custom Base-62 alphabet,
 * regardless of the native UTF-16 code point order in standard JS comparisons.
 */
export function compareBases(a: string, b: string): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const valA = CHAR_INDEX[a[i]] ?? -1;
    const valB = CHAR_INDEX[b[i]] ?? -1;
    if (valA !== valB) {
      return valA < valB ? -1 : 1;
    }
  }
  if (a.length < b.length) return -1;
  if (a.length > b.length) return 1;
  return 0;
}

/**
 * Generates a lexicographical midpoint string between prev and next using Base62.
 * Infinite subdivisions are supported by expanding string lengths when character spaces run out.
 */
export function getLexicalMidpoint(
  prev: string | null,
  next: string | null,
): string {
  // 1. Validation guard using our custom Base62 comparator
  if (prev && next && compareBases(prev, next) >= 0) {
    return prev + ALPHABET[Math.floor(ALPHABET.length / 2)];
  }

  const p = prev || "0";
  const n = next || "z";

  let i = 0;
  let midpoint = "";

  while (true) {
    // Find character rank indices from our fast precomputed index map
    const charP = p.length > i ? (CHAR_INDEX[p[i]] ?? -1) : -1;
    const charN =
      next === null
        ? ALPHABET.length
        : n.length > i
          ? (CHAR_INDEX[n[i]] ?? -1)
          : ALPHABET.length - 1;

    if (charN - charP > 1) {
      // There is gap space! Find the middle digit index
      const midIndex = Math.floor((charP + charN) / 2);
      midpoint += ALPHABET[midIndex];
      break;
    } else {
      // No space between chars at current position. Lock in this character and go to next depth
      midpoint += ALPHABET[charP === -1 ? 0 : charP];
      i++;
    }
  }

  return midpoint;
}

/**
 * Generates a URL-safe lexicographical midpoint between two arbitrary tie-breaker suffixes.
 * This prevents awkward, unreadable symbols (like space, quote, or hash characters) from being
 * generated in database keys, logs, or URLs while maintaining strict lexicographical bounds.
 */
export function getLexicalMidpointSuffix(
  prev: string | null,
  next: string | null,
): string {
  if (prev && next && prev >= next) {
    return prev + SUFFIX_ALPHABET[Math.floor(SUFFIX_ALPHABET.length / 2)];
  }

  const p = prev || "-";
  const n = next || "z";

  let i = 0;
  let midpoint = "";

  while (true) {
    const charP = p.length > i ? (SUFFIX_CHAR_INDEX[p[i]] ?? -1) : -1;
    const charN =
      next === null
        ? SUFFIX_ALPHABET.length
        : n.length > i
          ? (SUFFIX_CHAR_INDEX[n[i]] ?? -1)
          : SUFFIX_ALPHABET.length - 1;

    if (charN - charP > 1) {
      const midIndex = Math.floor((charP + charN) / 2);
      midpoint += SUFFIX_ALPHABET[midIndex];
      break;
    } else {
      midpoint += SUFFIX_ALPHABET[charP === -1 ? 0 : charP];
      i++;
    }
  }

  return midpoint;
}

/**
 * Combines the calculated lexical midpoint with a unique block ID as a tie-breaker suffix.
 * This guarantees that block positions are always globally unique and sortable,
 * resolving concurrent insertion races deterministically.
 */
export function generateBlockPosition(
  prevPosition: string | null,
  nextPosition: string | null,
  blockId: string,
): string {
  const cleanPrev = stripEpoch(prevPosition);
  const cleanNext = stripEpoch(nextPosition);

  // Safe base extraction using indexOf("_") instead of split to support underscores in block IDs
  let prevBase: string | null = null;
  if (cleanPrev) {
    const idx = cleanPrev.indexOf("_");
    prevBase = idx === -1 ? cleanPrev : cleanPrev.substring(0, idx);
  }

  let nextBase: string | null = null;
  if (cleanNext) {
    const idx = cleanNext.indexOf("_");
    nextBase = idx === -1 ? cleanNext : cleanNext.substring(0, idx);
  }

  if (prevBase && nextBase && prevBase === nextBase) {
    // SPECIAL COLLISION CASE: Suffixes are parsed, and we calculate a URL-safe
    // midpoint between them to guarantee that the new block sorts strictly inside the bounds.
    const prevSuffix = cleanPrev
      ? cleanPrev.substring(prevBase.length + 1)
      : "";
    const nextSuffix = cleanNext
      ? cleanNext.substring(nextBase.length + 1)
      : "";

    const midSuffix = getLexicalMidpointSuffix(prevSuffix, nextSuffix);
    return addEpoch(`${prevBase}_${midSuffix}_${blockId}`);
  }

  const midpoint = getLexicalMidpoint(prevBase, nextBase);
  return addEpoch(`${midpoint}_${blockId}`);
}

/**
 * Converts an integer into a fixed-length, zero-padded Base-62 string.
 * This ensures that strings of the same length sort 100% correctly lexicographically
 * and stay extremely short (only 6 characters represent up to 56,800,235,584 blocks).
 */
export function toBase62String(num: number, length: number = 6): string {
  let str = "";
  let temp = num;
  for (let i = 0; i < length; i++) {
    const charIndex = temp % 62;
    str = ALPHABET[charIndex] + str;
    temp = Math.floor(temp / 62);
  }
  return str;
}

/**
 * Assures that all blocks in an array have a valid, sorted position key.
 * Uses TypeScript generics to ensure absolute type safety across any state structure.
 */
export function assignInitialPositions<
  T extends { id: string; position?: string },
>(blocks: T[]): T[] {
  const count = blocks.length;
  if (count === 0) return [];

  // We divide the total Base-62 capacity of a 6-character string (56,800,235,584) by (count + 1)
  // to distribute and space them out evenly with massive margins in between.
  const TOTAL_CAPACITY = 62 ** 6;
  const step = Math.floor(TOTAL_CAPACITY / (count + 1)) || 1;

  return blocks.map((block, index) => {
    if (block.position) return block; // Already has a position, keep it

    const numericPosition = (index + 1) * step;
    const base = toBase62String(numericPosition, 6);
    const position = addEpoch(`${base}_${block.id}`);

    return { ...block, position };
  });
}

/**
 * Safely compares two position strings character-by-character using our custom alphabet.
 * This completely avoids locale-specific sorting discrepancies or ASCII-codepoint bias.
 */
export function comparePositions(
  posA: string | undefined | null,
  posB: string | undefined | null,
): number {
  const cleanA = stripEpoch(posA) || "";
  const cleanB = stripEpoch(posB) || "";

  const idxA = cleanA.indexOf("_");
  const idxB = cleanB.indexOf("_");

  const baseA = idxA === -1 ? cleanA : cleanA.substring(0, idxA);
  const baseB = idxB === -1 ? cleanB : cleanB.substring(0, idxB);

  // 1. Compare the base parts
  const baseCompare = compareBases(baseA, baseB);
  if (baseCompare !== 0) return baseCompare;

  // Compare suffixes as raw strings (IDs are not Base62 alphabet guaranteed)
  const suffixA = idxA === -1 ? "" : cleanA.substring(idxA + 1);
  const suffixB = idxB === -1 ? "" : cleanB.substring(idxB + 1);

  if (suffixA < suffixB) return -1;
  if (suffixA > suffixB) return 1;
  return 0;
}

/**
 * Rebalances and compacts all block positions in an array.
 * Over time, concentrated midpoints (like nested splits) can cause position strings to grow long (bloat).
 * This function resets all positions to clean, short, evenly-spaced sequences (000000 to zzzzzz)
 * while preserving the absolute relative visual order of all blocks.
 */
export function rebalancePositions<T extends { id: string; position?: string }>(
  blocks: T[],
): T[] {
  // 1. Sort the blocks in their true visual order using our safe custom comparator
  const sortedBlocks = [...blocks].sort((a, b) =>
    comparePositions(a.position, b.position),
  );

  const count = sortedBlocks.length;
  if (count === 0) return [];

  const TOTAL_CAPACITY = 62 ** 6;
  const step = Math.floor(TOTAL_CAPACITY / (count + 1)) || 1;

  // 2. Re-space them evenly using short padded Base-62 strings
  return sortedBlocks.map((block, index) => {
    const numericPosition = (index + 1) * step;
    const base = toBase62String(numericPosition, 6);
    const position = addEpoch(`${base}_${block.id}`);

    return { ...block, position };
  });
}

/**
 * Generates a position for a newly created block, with automatic, self-healing list rebalancing.
 * If the generated position's length exceeds a safety threshold (e.g., 12 characters),
 * it automatically triggers a high-speed list rebalance, compressing every block back to exactly 6 characters.
 */
export function generateBlockPositionWithAutoRebalance<
  T extends { id: string; position?: string },
>(
  blocks: T[],
  prevPosition: string | null,
  nextPosition: string | null,
  newBlockId: string,
  maxLengthThreshold: number = 12,
): { position: string; rebalancedBlocks?: T[] } {
  // 1. First, calculate the position normally using standard fractional indexing
  const position = generateBlockPosition(prevPosition, nextPosition, newBlockId);
  const cleanPos = stripEpoch(position) || "";
  const idx = cleanPos.indexOf("_");
  const cleanBase = idx === -1 ? cleanPos : cleanPos.substring(0, idx);

  // 2. If the coordinate base is within our safety limit, return it immediately (no rebalance needed!)
  if (cleanBase.length <= maxLengthThreshold) {
    return { position };
  }

  // 3. String length exceeded! Trigger automatic self-healing rebalance of the entire list
  const sorted = [...blocks].sort((a, b) =>
    comparePositions(a.position, b.position),
  );

  // Find where the new block should be inserted visually
  let insertIndex = sorted.length;
  if (prevPosition) {
    const idx = sorted.findIndex((b) => b.position === prevPosition);
    if (idx !== -1) insertIndex = idx + 1;
  } else if (nextPosition) {
    const idx = sorted.findIndex((b) => b.position === nextPosition);
    if (idx !== -1) insertIndex = idx;
  } else {
    insertIndex = 0;
  }

  // Construct a temporary representation of our new block
  const tempBlock = { id: newBlockId, position } as T;
  const listWithNewBlock = [
    ...sorted.slice(0, insertIndex),
    tempBlock,
    ...sorted.slice(insertIndex),
  ];

  // Rebalance the entire list (including our new block!)
  const rebalanced = rebalancePositions(listWithNewBlock);

  // Extract the newly computed position for our created block
  const rebalancedNewBlock = rebalanced.find((b) => b.id === newBlockId);
  const finalPosition = rebalancedNewBlock?.position || position;

  return {
    position: finalPosition,
    rebalancedBlocks: rebalanced,
  };
}
